import bearing from '@turf/bearing';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import destination from '@turf/destination';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { multiPolygonToLine } from '@turf/polygon-to-line';
import { BADGE_ENHANCER_SIZES, FixedMarker, KIND } from 'baseui/map-marker';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE, SIZE } from 'baseui/modal';
import L, { GeoJSON } from 'leaflet';
import { ComponentChildren, Fragment, h } from 'preact';
import { createPortal, useRef } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Circle, LayerGroup, Marker, useMapEvents } from 'react-leaflet';

import CafeIcon from '../icons/Cafe';
import NYC from '../../assets/nyc.geojson.json';
import { exprToTuple } from '../../utils/funcs';
import { Cafe, PointsOfInterest, usePointsOfInterest, usePointsOfInterestUpdater } from './PointsOfInterestProvider';

type ErrorModal = {
    header: string;
    body: ComponentChildren;
};

const BOUNDS = {
    MAX_RADIUS: 2000,
    MULTI_LINESTRING: multiPolygonToLine(NYC.features[0] as GeoJSON.Feature<GeoJSON.MultiPolygon>),
    MULTI_POLYGON: NYC.features[0] as GeoJSON.Feature<GeoJSON.MultiPolygon>,
};
const CAFE_MARKER_CONTAINERS = Array(5).fill(null).map(() => L.DomUtil.create('div', 'cafe-marker'));
const CAFE_MARKER_ICONS = CAFE_MARKER_CONTAINERS.map(container => L.divIcon({
    className: 'cafe-marker',
    html: container,
    iconAnchor: [6, 58],
}));
const DRAG_MARKER_ICON = L.divIcon({
    className: 'leaflet-div-icon resizer',
    iconSize: [12, 12],
});
const GEOLOCATION_ERROR: ErrorModal = {
    header: "We couldn't find you!",
    body: (<p>
        It looks like we couldn't geolocate you. Don't worry though, you can still search by manually dropping a pin on
        the map within the highlighted area!
    </p>),
};
const MANUAL_OUT_OF_BOUNDS_ERROR: ErrorModal = {
    header: 'Hark! Here be dragons!',
    body: (<>
        <p>
            That's outside our coverage area! Unfortunately, we currently only support a limited search area. Sorry
            about that.
        </p>
        <p>
            We're working to expand our searchable area. In the meanwhile, you can still search by manually dropping a
            pin on the map within the highlighted area with a single click!
        </p>
    </>),
};
const OUT_OF_BOUNDS_ERROR: ErrorModal = {
    header: 'We found you, but...',
    body: (<>
        <p>
            You're outside our coverage area! Unfortunately, we currently only support a limited search area. Sorry
            about that.
        </p>
        <p>
            We're working to expand our searchable area. In the meanwhile, you can still search by manually dropping a
            pin on the map within the highlighted area with a single click!
        </p>
    </>),
};
const USER_MARKER_CONTAINER = L.DomUtil.create('div', 'user-marker');
const USER_MARKER_ICON = L.divIcon({
    className: 'user-marker',
    html: USER_MARKER_CONTAINER,
    iconAnchor: [30, 58],
});

export default function SearchArea({ interactive }: { interactive: boolean }) {
    const poi = usePointsOfInterest();
    const setPoi = usePointsOfInterestUpdater();

    const [errorModal, setErrorModal] = useState<ErrorModal>(null);
    const setUserOrElseShowError = useMemo(() => ($e: { latlng: L.LatLng }, err: ErrorModal) => {
        if (_isPointInBounds($e.latlng)) setPoi({ ...poi, user: { ...poi.user, center: $e.latlng } });
        else setErrorModal(err);
    }, [poi, setPoi]);

    const map = useMapEvents({
        click($e) {
            if (interactive && poi.user.center == null) setUserOrElseShowError($e, MANUAL_OUT_OF_BOUNDS_ERROR);
        },
        locationerror() {
            setErrorModal(GEOLOCATION_ERROR);
        },
        locationfound($e) {
            setUserOrElseShowError($e, OUT_OF_BOUNDS_ERROR);
        },
    });

    useEffect(() => {
        if (!interactive) {
            const circle = L.circle(
                poi.user.center,
                { fillOpacity: 0, opacity: 0, radius: poi.user.radius + 100 },
            ).addTo(map);
            const bounds = circle.getBounds();
            circle.removeFrom(map);
            map.flyToBounds(bounds);
        }
    }, [interactive]);

    return (<>
        {poi.user.center && <UserMarker interactive={interactive} />}
        {poi.cafes.map((cafe, i) => <CafeMarker key={cafe.id} cafe={cafe} i={i} />)}
        <Modal animate
               autoFocus
               closeable
               isOpen={!!errorModal}
               onClose={() => setErrorModal(null)}
               role={ROLE.dialog}
               size={SIZE.default}>
            <ModalHeader>{errorModal?.header}</ModalHeader>
            <ModalBody>{errorModal?.body}</ModalBody>
            <ModalFooter>
                <ModalButton onClick={() => setErrorModal(null)}>Okay</ModalButton>
            </ModalFooter>
        </Modal>
    </>);
}

function CafeMarker({ cafe, i }: { cafe: Cafe; i: number; }) {
    const { highlightedCafe } = usePointsOfInterest();
    const setPoi = usePointsOfInterestUpdater();

    return (
        <Marker eventHandlers={{
            click() {
                setPoi(poi => ({ ...poi, highlightedCafe: i }));
            },
        }} icon={CAFE_MARKER_ICONS[i]} position={cafe.position} riseOnHover>
            {createPortal(
                <FixedMarker badgeEnhancerContent={() => i + 1}
                             badgeEnhancerSize={BADGE_ENHANCER_SIZES.small}
                             kind={highlightedCafe === i ? KIND.accent : KIND.default}
                             startEnhancer={CafeIcon} />,
                CAFE_MARKER_CONTAINERS[i],
            )}
        </Marker>
    );
}

function UserMarker({ interactive }: { interactive: boolean }) {
    const { user } = usePointsOfInterest();
    const setPoi = usePointsOfInterestUpdater();

    const circleRef = useRef<L.Circle>(null);
    const [dragMarkerPosition, setDragMarkerPosition] = useState(() => _computeDragHandlePosition(user));
    const [isDragging, setIsDragging] = useState(false);

    return (<LayerGroup>
        {interactive && <Circle center={user.center} radius={user.radius} ref={circleRef} />}
        {interactive && !isDragging &&
            <Marker draggable
                    eventHandlers={{
                        drag($e) {
                            const center = L.latLng(user.center);
                            let dist = center.distanceTo($e.latlng);
                            if (dist > BOUNDS.MAX_RADIUS) {
                                dist = BOUNDS.MAX_RADIUS;
                                $e.target.setLatLng(destination(
                                    [center.lng, center.lat],
                                    dist / 1000,
                                    bearing(
                                        [center.lng, center.lat],
                                        [$e.latlng.lng, $e.latlng.lat],
                                    )
                                ).geometry.coordinates.reverse());
                            }
                            setPoi(poi => ({ ...poi, user: { ...poi.user, radius: dist } }));
                        },
                        dragend($e) {
                            setDragMarkerPosition($e.target.getLatLng());
                        },
                    }}
                    icon={DRAG_MARKER_ICON}
                    position={dragMarkerPosition} />
        }
        <Marker autoPan
                draggable={interactive}
                eventHandlers={{
                    drag($e) {
                        setPoi(poi => ({ ...poi, user: { ...poi.user, center: $e.latlng } }));
                    },
                    dragend($e) {
                        const latlng = $e.target.getLatLng();
                        const [lat, lng] = _nearestPointInBounds(latlng);

                        if (latlng.lat !== lat || latlng.lng !== lng) $e.target.setLatLng([lat, lng]);
                        setPoi(poi => ({ ...poi, user: { ...poi.user, center: [lat, lng] } }));
                        setDragMarkerPosition(_computeDragHandlePosition({ center: [lat, lng], radius: user.radius }));
                        setIsDragging(false);
                    },
                    dragstart() {
                        setIsDragging(true);
                    },
                }}
                icon={USER_MARKER_ICON}
                position={user.center}
                riseOnHover />
        {createPortal(<FixedMarker dragging={isDragging} label="start" />, USER_MARKER_CONTAINER)}
    </LayerGroup>);
}

function _computeDragHandlePosition({ center, radius }: PointsOfInterest['user']): L.LatLngTuple {
    return destination(
        exprToTuple(center).reverse(),
        radius / 1000,
        90,
    ).geometry.coordinates.reverse() as L.LatLngTuple;
}

function _isPointInBounds(p: L.LatLng) {
    return booleanPointInPolygon([p.lng, p.lat], BOUNDS.MULTI_POLYGON);
}

function _nearestPointInBounds(p: L.LatLng): L.LatLngTuple {
    if (_isPointInBounds(p)) return [p.lat, p.lng];
    return BOUNDS.MULTI_LINESTRING.features
        .map(f => nearestPointOnLine(f, [p.lng, p.lat]))
        .reduce((acc, cur) => acc.properties.dist < cur.properties.dist ? acc : cur)
        .geometry
        .coordinates
        .reverse() as L.LatLngTuple;
}
